import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import config from '../../config';
import { useRoomNames } from './useRoomNames';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const addOneDay = (date) => {
  if (!date) return null;
  return dayjs(date).add(1, 'day');
};
const company = JSON.parse(sessionStorage.getItem('companyDetails'));
export default function HoldRoom() {
  const { roomLabel } = useRoomNames();
  const [step, setStep] = useState(1);

  const [hotels, setHotels] = useState([]);
  const [search, setSearch] = useState({
    hotelId: '',
    fromDate: dayjs(),
    toDate: dayjs().add(1, 'day'),
  });

  const [rooms, setRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);

  // Multi-select
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // Bulk action dropdown
  const [bulkAction, setBulkAction] = useState('');

  // Modal for bulk action
  const [modal, setModal] = useState({
    open: false,
    type: '', // "hold" or "maintenance"
    data: {
      name: '',
      mobile: '',
      address: '',
      city: '',
      state: '',
      remark: '',
    },
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [submitting, setSubmitting] = useState(false);

  const token = sessionStorage.getItem('token');

  // Prefill coming from the Dashboard calendar "Hold Room" action.
  const location = useLocation();
  const prefillRef = useRef(false);

  useEffect(() => {
    if (!token) return;

    fetch(`${config.API_BASE_URL}/Home/GetHotelsdropdown/${company?.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => setHotels(data || []))
      .catch((err) => console.error('Failed to load hotels:', err));
  }, [token]);

  const fetchRooms = async () => {
    if (!search.hotelId || !search.fromDate || !search.toDate) return;

    setLoadingRooms(true);
    try {
      const from = search.fromDate.format('YYYY-MM-DD');
      const to = search.toDate.format('YYYY-MM-DD');

      const url = `${config.API_BASE_URL}/Home/Allavailablerooms/${search.hotelId}/${from}/${to}/${company?.id}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) throw new Error('Failed to fetch rooms');

      const data = await res.json();
      setRooms(data || []);
      setSelectedRooms([]);
      setSelectAll(false);
      setBulkAction('');
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: 'Failed to load rooms',
        severity: 'error',
      });
    } finally {
      setLoadingRooms(false);
    }
  };

  useEffect(() => {
    if (step === 2 && search.hotelId) fetchRooms();
  }, [step, search.hotelId, search.fromDate, search.toDate]);

  // ── Calendar prefill ────────────────────────────────────────────────
  // Apply From/To dates from the calendar's "Hold Room" action once.
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf || prefillRef.current) return;
    prefillRef.current = true;
    setSearch((s) => ({
      ...s,
      fromDate: pf.fromDate ? dayjs(pf.fromDate) : s.fromDate,
      toDate: pf.toDate ? dayjs(pf.toDate) : s.toDate,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // When the hotel list is available and a single hotel exists, auto-select it
  // and jump to step 2 (the effect above then loads its rooms). For multi-hotel
  // the user picks the hotel — the room still pre-selects on load (below).
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf || !hotels.length || search.hotelId) return;
    if (hotels.length === 1) {
      setSearch((s) => ({ ...s, hotelId: hotels[0].id }));
      setStep(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels]);

  // Pre-select the clicked room once its hotel's room list has loaded.
  useEffect(() => {
    const pf = location.state?.prefill;
    if (!pf?.roomNumber || !rooms.length) return;
    const match = rooms.find(
      (r) => String(r.roomnumber) === String(pf.roomNumber),
    );
    if (match && !selectedRooms.some((r) => r.roomnumber === match.roomnumber)) {
      setSelectedRooms([match]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);

  const handleSelectRoom = (room) => {
    if (selectedRooms.some((r) => r.roomnumber === room.roomnumber)) {
      setSelectedRooms(
        selectedRooms.filter((r) => r.roomnumber !== room.roomnumber),
      );
    } else {
      setSelectedRooms([...selectedRooms, room]);
    }
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedRooms(rooms.filter((r) => r.status === 'Available'));
    } else {
      setSelectedRooms([]);
    }
  };

  const handleBulkActionChange = (e) => {
    const action = e.target.value;
    setBulkAction(action);
    if (action && selectedRooms.length > 0) {
      setModal({
        open: true,
        type: action,
        data: {
          name: '',
          mobile: '',
          address: '',
          city: '',
          state: '',
          remark: '',
        },
      });
    }
  };

  // Submit Maintenance (bulk)
  const submitMaintenance = async () => {
    const { data } = modal;
    if (!data.remark.trim()) {
      setSnackbar({
        open: true,
        message: 'Remark is required for Maintenance',
        severity: 'warning',
      });
      return;
    }

    setSubmitting(true);

    try {
      const roomNumbers = selectedRooms.map((r) => r.roomnumber);

      const payload = {
        HotelId: parseInt(search.hotelId),
        RoomNumbers: roomNumbers,
        CheckIn: search.fromDate.format('YYYY-MM-DD'),
        CheckOut: search.toDate.format('YYYY-MM-DD'),
        Remark: data.remark.trim(),
        Companyid: company?.id,
      };

      const res = await fetch(
        `${config.API_BASE_URL}/Home/MarkRoomMaintenance`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Maintenance failed');
      }

      setSnackbar({
        open: true,
        message: `${selectedRooms.length} room(s) marked as Maintenance`,
        severity: 'success',
      });

      setModal({ open: false, type: '', data: {} });
      setSelectedRooms([]);
      setSelectAll(false);
      setBulkAction('');
      fetchRooms();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Submit Hold (bulk)
  const submitHold = async () => {
    const { data } = modal;

    if (!data.name.trim() || !data.mobile.trim()) {
      setSnackbar({
        open: true,
        message: 'Name & Mobile are required',
        severity: 'warning',
      });
      return;
    }

    setSubmitting(true);

    try {
      const roomNumbers = selectedRooms.map((r) => r.roomnumber);

      const payload = {
        HotelId: parseInt(search.hotelId),
        RoomNumbers: roomNumbers,
        CheckIn: search.fromDate.format('YYYY-MM-DD'),
        CheckOut: search.toDate.format('YYYY-MM-DD'),
        Name: data.name,
        Mobile: data.mobile,
        Address: data.address || null,
        City: data.city || null,
        State: data.state || null,
        Remark: data.remark.trim() || null,
        Companyid: company?.id,
      };

      const res = await fetch(`${config.API_BASE_URL}/Home/MarkRoomHold`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errText = await res.text();
        throw new Error(errText || 'Hold failed');
      }

      setSnackbar({
        open: true,
        message: `${selectedRooms.length} room(s) put on Hold`,
        severity: 'success',
      });

      setModal({ open: false, type: '', data: {} });
      setSelectedRooms([]);
      setSelectAll(false);
      setBulkAction('');
      fetchRooms();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  // Unified submit handler
  const handleSubmit = () => {
    if (modal.type === 'maintenance') {
      submitMaintenance();
    } else if (modal.type === 'hold') {
      submitHold();
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth='lg' sx={{ mt: 3, mb: 12 }}>
        <Typography variant='h6' textAlign='center' fontWeight='300' mb={4}>
          Room Hold / Maintenance
        </Typography>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}>
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Step 1 */}
        {step === 1 && (
          <Paper sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            <Typography variant='h7' gutterBottom>
              Select Hotel & Dates
            </Typography>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Hotel</InputLabel>
              <Select
                value={search.hotelId}
                label='Hotel'
                onChange={(e) =>
                  setSearch({ ...search, hotelId: e.target.value })
                }>
                <MenuItem value=''>Select Hotel</MenuItem>
                {hotels.map((h) => (
                  <MenuItem key={h.id} value={h.id}>
                    {h.hotelName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <DatePicker
                  label='From Date'
                  value={search.fromDate}
                  minDate={dayjs('1900-01-01')}
                  maxDate={dayjs('2100-12-31')}
                  onChange={(newVal) =>
                    setSearch({
                      ...search,
                      fromDate: newVal,
                      toDate:
                        dayjs.max(newVal, search.toDate) || addOneDay(newVal),
                    })
                  }
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              <Grid item xs={6}>
                <DatePicker
                  label='To Date'
                  value={search.toDate}
                  minDate={search.fromDate}
                  maxDate={dayjs('2100-12-31')}
                  onChange={(newVal) =>
                    setSearch({ ...search, toDate: newVal })
                  }
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
            </Grid>

            <Button
              variant='contained'
              fullWidth
              size='medium'
              sx={{ mt: 3 }}
              disabled={!search.hotelId || !search.fromDate || !search.toDate}
              onClick={() => setStep(2)}>
              Show Available Rooms
            </Button>
          </Paper>
        )}

        {/* Step 2 */}
        {step === 2 && (
          <Paper sx={{ p: 3 }}>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              mb={2}
              flexWrap='wrap'>
              <Typography variant='h6'>
                Rooms ({search.fromDate.format('DD MMM')} –{' '}
                {search.toDate.format('DD MMM')})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  variant='outlined'
                  size='small'
                  onClick={() => setStep(1)}>
                  Change
                </Button>
                <FormControl size='small' sx={{ minWidth: 140 }}>
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={bulkAction}
                    label='Action'
                    onChange={handleBulkActionChange}
                    disabled={selectedRooms.length === 0}>
                    <MenuItem value=''>Select Action</MenuItem>
                    <MenuItem value='maintenance'>Maintenance</MenuItem>
                    <MenuItem value='hold'>Hold</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  disabled={loadingRooms}
                />
              }
              label='Select All Available Rooms'
              sx={{ mb: 2 }}
            />

            {loadingRooms ? (
              <Box display='flex' justifyContent='center' my={8}>
                <CircularProgress />
              </Box>
            ) : rooms.length === 0 ? (
              <Alert severity='info' sx={{ maxWidth: 500, mx: 'auto' }}>
                No available rooms found.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {rooms.map((room) => {
                  const isSelected = selectedRooms.some(
                    (r) => r.roomnumber === room.roomnumber,
                  );
                  const isDisabled = room.status !== 'Available';

                  return (
                    <Grid item xs={12} sm={6} md={4} key={room.roomnumber}>
                      <Box
                        sx={{
                          p: 2,
                          border: `2px solid ${isSelected ? '#1976d2' : room.status === 'Available' ? '#4caf50' : '#ff9800'}`,
                          borderRadius: 2,
                          bgcolor: isSelected
                            ? '#e3f2fd'
                            : room.status === 'Available'
                              ? '#f0fdf4'
                              : '#fff8e1',
                          position: 'relative',
                          cursor: isDisabled ? 'not-allowed' : 'pointer',
                          opacity: isDisabled ? 0.6 : 1,
                        }}
                        onClick={() => !isDisabled && handleSelectRoom(room)}>
                        <Box display='flex' alignItems='center'>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectRoom(room)}
                            disabled={isDisabled}
                            sx={{ p: 0.5, mr: 1 }}
                          />
                          <div>
                            <Typography variant='h6' fontWeight='bold'>
                              {roomLabel(room.roomnumber)}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                              {room.roomTypeName}
                            </Typography>
                          </div>
                        </Box>

                        {isSelected && (
                          <Typography
                            variant='caption'
                            color='primary'
                            sx={{ mt: 1, display: 'block' }}>
                            Selected
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Paper>
        )}

        {/* Bulk Action Modal */}
        <Dialog
          open={modal.open}
          onClose={() => !submitting && setModal({ ...modal, open: false })}
          maxWidth='sm'
          fullWidth>
          <DialogTitle>
            {modal.type === 'maintenance' ? 'Mark Maintenance' : 'Put on Hold'}{' '}
            - {selectedRooms.length} Room(s)
          </DialogTitle>
          <DialogContent>
            {modal.type === 'hold' && (
              <>
                <TextField
                  label='Name *'
                  value={modal.data.name}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, name: e.target.value },
                    })
                  }
                  fullWidth
                  required
                  size='small'
                  sx={{ mt: 2 }}
                  disabled={submitting}
                />

                <TextField
                  label='Mobile *'
                  value={modal.data.mobile}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, mobile: e.target.value },
                    })
                  }
                  fullWidth
                  required
                  size='small'
                  sx={{ mt: 1.5 }}
                  disabled={submitting}
                />

                <TextField
                  label='Address'
                  value={modal.data.address}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, address: e.target.value },
                    })
                  }
                  fullWidth
                  multiline
                  rows={2}
                  size='small'
                  sx={{ mt: 1.5 }}
                  disabled={submitting}
                />

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      label='City'
                      value={modal.data.city}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: { ...modal.data, city: e.target.value },
                        })
                      }
                      fullWidth
                      size='small'
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label='State'
                      value={modal.data.state}
                      onChange={(e) =>
                        setModal({
                          ...modal,
                          data: { ...modal.data, state: e.target.value },
                        })
                      }
                      fullWidth
                      size='small'
                      disabled={submitting}
                    />
                  </Grid>
                </Grid>
              </>
            )}

            <TextField
              label={
                modal.type === 'maintenance' ? 'Remark *' : 'Remark (optional)'
              }
              value={modal.data.remark}
              onChange={(e) =>
                setModal({
                  ...modal,
                  data: { ...modal.data, remark: e.target.value },
                })
              }
              fullWidth
              multiline
              rows={3}
              size='small'
              required={modal.type === 'maintenance'}
              sx={{ mt: 2 }}
              disabled={submitting}
            />
          </DialogContent>
          <DialogActions>
            <Button
              size='small'
              onClick={() => setModal({ ...modal, open: false })}
              disabled={submitting}>
              Cancel
            </Button>
            <Button
              variant='contained'
              size='small'
              onClick={handleSubmit}
              disabled={submitting}>
              {submitting ? (
                <CircularProgress size={20} />
              ) : (
                'Confirm for All Selected'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </LocalizationProvider>
  );
}
